import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  ArrowRightIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { Modal, Button, Badge, Card, CardBody, Spinner } from '../ui';

interface DealPreviewModalProps {
  dealId: number;
  dealDescription: string;
  dealAmount: number;
  dealCurrency: string;
  clientName?: string;
  isOpen: boolean;
  onClose: () => void;
}

interface DealStats {
  activeTasks: number;
  completedTasks: number;
  status: string;
}

const DealPreviewModal: React.FC<DealPreviewModalProps> = ({
  dealId,
  dealDescription,
  dealAmount,
  dealCurrency,
  clientName,
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DealStats>({ activeTasks: 0, completedTasks: 0, status: 'Active' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadDealStats();
    }
  }, [isOpen, dealId]);

  const loadDealStats = async () => {
    try {
      setLoading(true);
      // TODO: Replace with actual API call to get deal stats
      // For now, using mock data
      setStats({
        activeTasks: 4,
        completedTasks: 8,
        status: 'In Progress',
      });
    } catch (error) {
      console.error('Error loading deal stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = () => {
    onClose();
    navigate(`/admin/deals/${dealId}`);
  };

  const getStatusVariant = (status: string): 'success' | 'error' | 'primary' | 'neutral' => {
    switch (status.toLowerCase()) {
      case 'won':
        return 'success';
      case 'lost':
        return 'error';
      case 'in progress':
        return 'primary';
      default:
        return 'neutral';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Deal Overview" size="md">
      {/* Deal Info */}
      <div className="mb-6">
        <div className="flex items-start mb-3">
          <div className="flex-shrink-0 h-12 w-12 rounded-full bg-success-100 flex items-center justify-center">
            <CurrencyDollarIcon className="h-6 w-6 text-success-600" />
          </div>
          <div className="ml-4 flex-1">
            <h3 className="text-base font-medium text-neutral-900">{dealDescription}</h3>
            <div className="text-2xl font-bold text-success-600 mt-1">
              ${dealAmount.toLocaleString()} {dealCurrency}
            </div>
          </div>
        </div>

        {clientName && (
          <Card variant="bordered" className="bg-neutral-50">
            <CardBody className="p-3">
              <div className="flex items-center text-sm text-neutral-600">
                <BuildingOfficeIcon className="h-4 w-4 mr-2" />
                <span className="font-medium">Client:</span>
                <span className="ml-1">{clientName}</span>
              </div>
            </CardBody>
          </Card>
        )}
      </div>

      {/* Stats */}
      {loading ? (
        <div className="flex justify-center py-4">
          <Spinner size="md" />
        </div>
      ) : (
        <div className="space-y-3 mb-6">
          <Card variant="bordered" className="bg-neutral-50">
            <CardBody className="p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-neutral-700">Status</span>
                <Badge variant={getStatusVariant(stats.status)}>
                  {stats.status}
                </Badge>
              </div>
            </CardBody>
          </Card>
          <Card variant="bordered" className="bg-primary-50">
            <CardBody className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <ClockIcon className="h-5 w-5 text-primary-600 mr-2" />
                  <span className="text-sm font-medium text-neutral-700">Active Tasks</span>
                </div>
                <span className="text-lg font-bold text-primary-600">{stats.activeTasks}</span>
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      {/* Action Button */}
      <Button
        variant="success"
        onClick={handleViewDetails}
        className="w-full"
        rightIcon={<ArrowRightIcon className="h-4 w-4" />}
      >
        View Deal Details
      </Button>
    </Modal>
  );
};

export default DealPreviewModal;

